import { memo } from 'react';

import { type Board } from '@air/api/types';

import { ItemPickerBoardsSection } from '~/components/ItemPicker/ItemPickerBoardsSection';
import {
  ItemPickerGalleryView,
  type ItemPickerGalleryViewBoardsProps,
  type ItemPickerGalleryViewClipsProps,
} from '~/components/ItemPicker/ItemPickerGalleryView';
import { type ItemPickerNavView } from '~/components/ItemPicker/itemPickerTypes';

/** Stable empty list for the boards-less views so the gallery's memoized geometry never recomputes. */
const NO_BOARDS: Board[] = [];

/**
 * The single source for which entity types a navView renders — the branch conditions below derive
 * from these, and ItemPicker's marquee handlers use them so a replace-drag or background click only
 * touches selections the current view can express (a marquee in a boards-only library must not
 * wipe clip selections made in the Search view, and vice versa).
 */
export const doesNavViewShowBoards = (navView: ItemPickerNavView, areBoardsSelectable: boolean) =>
  navView.kind === 'library' ||
  navView.kind === 'board' ||
  (areBoardsSelectable && (navView.kind === 'all' || navView.kind === 'favorites' || navView.kind === 'recent'));

/** Libraries contain only boards (mirroring the app's library pages). */
export const doesNavViewShowClips = (navView: ItemPickerNavView, areClipsSelectable: boolean) =>
  areClipsSelectable && navView.kind !== 'library';

export type ItemPickerGalleryBoardHandlerProps = Pick<
  ItemPickerGalleryViewBoardsProps,
  'areBoardsSelectable' | 'onBoardClick' | 'onBoardDoubleClick' | 'registerBoardElement' | 'selectedBoardIds'
>;

export type ItemPickerContentProps = ItemPickerGalleryViewClipsProps &
  ItemPickerGalleryBoardHandlerProps & {
    navView: ItemPickerNavView;
  };

/**
 * The picker's content region — a pure navView switch that picks which gallery wrapper renders.
 * Selection and marquee state stay in ItemPicker, the smallest common owner of content + footer +
 * marquee; this component only threads that state through to the active branch.
 */
export const ItemPickerContent = memo(({ navView, ...galleryProps }: ItemPickerContentProps) => {
  const { areBoardsSelectable, areClipsSelectable } = galleryProps;
  const showBoards = doesNavViewShowBoards(navView, areBoardsSelectable);
  const showClips = doesNavViewShowClips(navView, areClipsSelectable);

  if (showBoards) {
    return <ItemPickerBoardsSection {...galleryProps} areClipsSelectable={showClips} navView={navView} />;
  }

  if (showClips) {
    return (
      <ItemPickerGalleryView
        {...galleryProps}
        boards={NO_BOARDS}
        boardsTotal={0}
        hasMoreBoards={false}
        isLoadingBoards={false}
      />
    );
  }

  return null;
});

ItemPickerContent.displayName = 'ItemPickerContent';
